// Wallet top-ups asked for at the kiosk (F-KIO-10; spec/12 §7.1, spec/06 §5, §9.3).
//
// The player on a station's session tops up their wallet from the kiosk Time screen's **Top up** tab. The station
// names only the amount, an optional offer and how it is paid; the player is its own session's (never an id the
// station sent), and the amount is checked against the organisation's wallet limits before anything is written.
// Nothing is credited until it is paid:
//
//   upi    a payment intent through the venue's online provider (`beginOnlineTopUp`); the kiosk shows its QR and the
//          provider's capture credits the wallet once, with tender `online` (spec/06 §9.3). Offered only when online
//          payments are open at the venue.
//   desk   a **top-up request** the counter completes: it waits in the console's Wallet page ("Asked for at a PC"),
//          and staff take the money by a counter tender, which tops the wallet up through the ordinary counter top-up
//          (`wallet.topUp`, audited, through the ledger). Staff can also cancel it.
//
//   POST /v1/station/wallet/top-ups                                       ask for a top-up (Idempotency-Key)
//   GET  /v1/station/wallet/top-ups/{topUpId}                             where it is (a lapsed QR closes as expired)
//   POST /v1/station/wallet/top-ups/{topUpId}/cancel                      the player cancels one still waiting
//   GET  /v1/organizations/{o}/wallet/top-up-requests                     the desk's list of waiting requests
//   POST /v1/organizations/{o}/wallet/top-up-requests/{requestId}/complete  staff take the money and credit it
//   POST /v1/organizations/{o}/wallet/top-up-requests/{requestId}/cancel    staff cancel it
import { createRoute } from '@hono/zod-openapi';
import { z } from "../zod.js";
import { PlayerIdSchema, WalletTopUpRequestIdSchema } from "../ids/schema.js";
import { commonProblemResponses, jsonResponse, problemResponse } from "../openapi/responses.js";
import { NonNegativeMoneySchema, PositiveMoneySchema } from "../primitives/money.js";
import { IsoDateTimeSchema } from "../primitives/scalars.js";
import { OrganizationScopeParamsSchema } from "../primitives/scope.js";
import { WALLET_TOPUP } from "../permissions/index.js";
import { PERMISSION_EXTENSION } from "./audit.js";
import { IdempotentCreateHeaderSchema } from "./id-checks.js";
import { StationPassPurchaseUpiSchema, StationScanToPaySchema } from "./passes.js";
const STATION_TOPUP_AUTH = 'Station-authenticated: `Authorization: Bearer <device token>` (spec/07 §5). The station provides its own ' +
    'organisation, venue and station from the token; the top-up is for the player on its own active session.';
/** How a kiosk top-up is paid (F-KIO-10): at the desk, or by UPI through the venue's online provider. */
export const StationWalletTopUpTenderSchema = z
    .enum(['desk', 'upi'])
    .openapi('StationWalletTopUpTender', { description: 'How a kiosk wallet top-up is paid (F-KIO-10).' });
/**
 * Where a kiosk top-up is (F-KIO-10). `awaiting_desk` and `awaiting_upi` are pending: nothing is credited.
 * `applied` once paid and the wallet credited; `cancelled` by the player or staff; `expired` when the UPI request
 * ran out before it was paid.
 */
export const StationWalletTopUpStatusSchema = z
    .enum(['awaiting_desk', 'awaiting_upi', 'applied', 'cancelled', 'expired'])
    .openapi('StationWalletTopUpStatus', { description: 'Where a kiosk wallet top-up is (F-KIO-10).' });
/** A wallet top-up asked for at the kiosk (F-KIO-10). */
export const StationWalletTopUpSchema = z
    .object({
    id: WalletTopUpRequestIdSchema,
    /** The cash to add. */
    amount: PositiveMoneySchema,
    /** The bonus the offer adds once it is paid; zero with no offer. */
    bonus: NonNegativeMoneySchema,
    tender: StationWalletTopUpTenderSchema,
    status: StationWalletTopUpStatusSchema,
    /** The QR to show while it is `awaiting_upi`; `null` otherwise. */
    upi: StationPassPurchaseUpiSchema.nullable(),
    /** The venue's own UPI QR while a "Scan & pay" top-up is `awaiting_desk` (F-PAY-02); absent otherwise. */
    scanToPay: StationScanToPaySchema.optional(),
    createdAt: IsoDateTimeSchema,
    appliedAt: IsoDateTimeSchema.nullable(),
})
    .openapi('StationWalletTopUp', { description: 'A wallet top-up asked for at the kiosk (F-KIO-10).' });
/**
 * The `station.wallet.topup` command payload (spec/09 §6.2, F-KIO-10): a top-up asked for at this PC moved —
 * applied once staff took the money at the desk (or the UPI capture landed), cancelled, or expired. The station
 * forwards it to the kiosk as `wallet/topUp` and, once `applied`, re-reads the session player's wallet and Account.
 */
export const StationWalletTopUpCommandSchema = z
    .object({ topUp: StationWalletTopUpSchema })
    .openapi('StationWalletTopUpCommand', { description: 'The `station.wallet.topup` command payload (spec/09 §6.2).' });
/**
 * Ask for a top-up at the kiosk: the cash to add (a tile, or an amount the player typed) and how it is paid. The bonus
 * is the server's to pick — the best `wallet.topupOffers` tier the amount reaches at the station's venue, the rule the
 * kiosk previewed it with (2026-10-01); `offerId`, which a kiosk names for older servers, is not trusted for it.
 */
export const RequestStationWalletTopUpSchema = z
    .object({
    amount: PositiveMoneySchema,
    tender: StationWalletTopUpTenderSchema,
    offerId: z.string().min(1).max(64).optional(),
    /**
     * "Scan & pay (UPI)" (F-PAY-02, 2026-10-01): with `tender: desk`, the player pays by scanning the venue's own UPI QR
     * at the PC and the desk confirms it; the top-up carries `scanToPay` while it waits. Ignored with `upi`, and when the
     * venue has no UPI ID (an ordinary desk top-up).
     */
    scanToPay: z.boolean().optional(),
})
    .strict()
    .openapi('RequestStationWalletTopUpRequest', { description: 'Ask for a wallet top-up at the kiosk (F-KIO-10).' });
const StationTopUpParamsSchema = z.object({ topUpId: WalletTopUpRequestIdSchema }).openapi('StationTopUpParams');
const topUpRefusals = [
    'wallet-disabled',
    'wallet-topup-below-minimum',
    'wallet-topup-above-maximum',
    'wallet-balance-over-maximum',
    'wallet-topup-tax-at-sale',
    'online-payments-unavailable',
    'pass-purchase-guest',
];
export const requestStationWalletTopUpRoute = createRoute({
    method: 'post',
    path: '/v1/station/wallet/top-ups',
    operationId: 'requestStationWalletTopUp',
    tags: ['wallet', 'stations'],
    summary: 'Ask for a wallet top-up from the kiosk (F-KIO-10)',
    description: `${STATION_TOPUP_AUTH} Checks the amount against the organisation’s wallet limits (\`wallet.minTopupAmount\`, ` +
        '`wallet.maxTopupAmount`, and `wallet.maxBalanceAmount` with the offer’s bonus) and names which one refused it. ' +
        '`upi` opens a payment intent and returns its QR — the wallet is credited once, when the provider reports the ' +
        'capture; `desk` leaves a request for the counter to complete. Only a registered player can top up here. Requires ' +
        'an `Idempotency-Key`: a retry answers the request already made. Audited.',
    request: {
        headers: IdempotentCreateHeaderSchema,
        body: { content: { 'application/json': { schema: RequestStationWalletTopUpSchema } }, required: true },
    },
    responses: {
        201: jsonResponse(StationWalletTopUpSchema, 'The top-up, waiting on the desk or the UPI payment.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('The station has no active session with a player.', 'not-found'),
        409: problemResponse('The wallet is off, the amount is outside its limits, or UPI is not open here.', ...topUpRefusals),
        422: problemResponse('The same key was already used for a different top-up, or the offer is not one of the organisation’s.', 'idempotency-key-reused', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const getStationWalletTopUpRoute = createRoute({
    method: 'get',
    path: '/v1/station/wallet/top-ups/{topUpId}',
    operationId: 'getStationWalletTopUp',
    tags: ['wallet', 'stations'],
    summary: 'Read a kiosk wallet top-up (F-KIO-10)',
    description: `${STATION_TOPUP_AUTH} Returns where the top-up is. An \`awaiting_upi\` one whose payment captured reads ` +
        '`applied`; one whose request expired, failed or was cancelled closes as `expired`. Only a top-up asked for on ' +
        'this station is visible.',
    request: { params: StationTopUpParamsSchema },
    responses: {
        200: jsonResponse(StationWalletTopUpSchema, 'The top-up.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('No such top-up on this station.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const cancelStationWalletTopUpRoute = createRoute({
    method: 'post',
    path: '/v1/station/wallet/top-ups/{topUpId}/cancel',
    operationId: 'cancelStationWalletTopUp',
    tags: ['wallet', 'stations'],
    summary: 'Cancel a pending kiosk wallet top-up (F-KIO-10)',
    description: `${STATION_TOPUP_AUTH} The player cancels a top-up still waiting on the desk or the UPI payment: an open payment ` +
        'intent is cancelled and nothing is credited. One already cancelled or expired is answered as it is; one already ' +
        'credited is refused. Audited.',
    request: { params: StationTopUpParamsSchema },
    responses: {
        200: jsonResponse(StationWalletTopUpSchema, 'The top-up, cancelled.'),
        400: commonProblemResponses[400],
        401: problemResponse('The caller is not an authenticated station.', 'unauthenticated'),
        404: problemResponse('No such top-up on this station.', 'not-found'),
        409: problemResponse('The top-up is already paid and credited.', 'wallet-topup-closed'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
// ---------------------------------------------------------------------------------------------
// The desk's side: the requests waiting at a venue, completed or cancelled by staff
// ---------------------------------------------------------------------------------------------
/** A tender the counter takes a kiosk top-up request's money by (spec/06 §9.1). */
export const KioskTopUpRequestTenderSchema = z
    .enum(['cash', 'counter_card', 'counter_upi'])
    .openapi('KioskTopUpRequestTender', { description: 'How the counter takes a kiosk top-up request’s money (F-KIO-10).' });
/** A top-up asked for at a PC, as the desk sees it (F-KIO-10). */
export const KioskTopUpRequestSchema = z
    .object({
    id: WalletTopUpRequestIdSchema,
    playerId: PlayerIdSchema,
    /** The player's display name; `null` when the profile carries none. */
    playerName: z.string().nullable(),
    /** The PC it was asked for at ("PC 07"). */
    stationName: z.string(),
    amount: PositiveMoneySchema,
    bonus: NonNegativeMoneySchema,
    status: StationWalletTopUpStatusSchema,
    /**
     * The "Scan & pay (UPI)" reference (F-PAY-02, 2026-10-01): the player chose to pay by scanning the venue's own UPI QR
     * at the PC, with this code as the payment's note — the desk matches it and completes with `counter_upi`. Absent for
     * an ordinary desk top-up.
     */
    scanToPayReference: z.string().optional(),
    createdAt: IsoDateTimeSchema,
})
    .openapi('KioskTopUpRequest', { description: 'A wallet top-up asked for at a PC, for the desk (F-KIO-10).' });
export const KioskTopUpRequestListSchema = z
    .object({ data: z.array(KioskTopUpRequestSchema) })
    .openapi('KioskTopUpRequestList', { description: 'The top-ups waiting on the desk at a venue (F-KIO-10).' });
/** Complete a request: the counter tender the money was taken by. */
export const CompleteKioskTopUpRequestSchema = z
    .object({ tender: KioskTopUpRequestTenderSchema })
    .strict()
    .openapi('CompleteKioskTopUpRequest', { description: 'Take a kiosk top-up request’s money at the counter (F-KIO-10).' });
const KioskTopUpRequestParamsSchema = OrganizationScopeParamsSchema.extend({ requestId: WalletTopUpRequestIdSchema }).openapi('KioskTopUpRequestParams');
export const listWalletTopUpRequestsRoute = createRoute({
    method: 'get',
    path: '/v1/organizations/{organizationId}/wallet/top-up-requests',
    operationId: 'listWalletTopUpRequests',
    tags: ['wallet'],
    summary: 'The kiosk top-ups waiting on the desk (F-KIO-10)',
    description: 'The top-ups players asked for at a PC to pay at the desk, still waiting, oldest first. Name the venue with ' +
        '`X-Clavian-Venue`. Requires `wallet:topup`, like a counter top-up.',
    [PERMISSION_EXTENSION]: WALLET_TOPUP,
    request: { params: OrganizationScopeParamsSchema },
    responses: {
        200: jsonResponse(KioskTopUpRequestListSchema, 'The waiting requests.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such organisation, or no venue you can see named in X-Clavian-Venue.', 'not-found'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const completeWalletTopUpRequestRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/wallet/top-up-requests/{requestId}/complete',
    operationId: 'completeWalletTopUpRequest',
    tags: ['wallet'],
    summary: 'Take a kiosk top-up’s money at the desk (F-KIO-10)',
    description: 'Staff took the money for a top-up asked for at a PC: the wallet is topped up by the counter tender, with the ' +
        'offer’s bonus, through the ledger — the ordinary counter top-up — and the request is marked applied. The limits ' +
        'are checked again now. Name the venue with `X-Clavian-Venue`. Requires `wallet:topup` and an `Idempotency-Key`: ' +
        'a retry answers the top-up already made. Audited.',
    [PERMISSION_EXTENSION]: WALLET_TOPUP,
    request: {
        params: KioskTopUpRequestParamsSchema,
        headers: IdempotentCreateHeaderSchema,
        body: { content: { 'application/json': { schema: CompleteKioskTopUpRequestSchema } }, required: true },
    },
    responses: {
        200: jsonResponse(KioskTopUpRequestSchema, 'The request, applied.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such request at the venue named in X-Clavian-Venue.', 'not-found'),
        409: problemResponse('The request is no longer waiting on the desk, the wallet is off, the top-up is now outside its limits, or cash is taken by someone holding no open drawer (ADR-0031).', 'cash-needs-shift', 'wallet-topup-closed', 'wallet-disabled', 'wallet-topup-below-minimum', 'wallet-topup-above-maximum', 'wallet-balance-over-maximum', 'wallet-topup-tax-at-sale'),
        422: problemResponse('The same key was already used for a different top-up.', 'idempotency-key-reused', 'unprocessable'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
export const cancelWalletTopUpRequestRoute = createRoute({
    method: 'post',
    path: '/v1/organizations/{organizationId}/wallet/top-up-requests/{requestId}/cancel',
    operationId: 'cancelWalletTopUpRequest',
    tags: ['wallet'],
    summary: 'Cancel a kiosk top-up waiting on the desk (F-KIO-10)',
    description: 'Staff cancel a top-up asked for at a PC that will not be paid: nothing is credited. One already cancelled is ' +
        'answered as it is; one already credited is refused. Name the venue with `X-Clavian-Venue`. Requires ' +
        '`wallet:topup`. Audited.',
    [PERMISSION_EXTENSION]: WALLET_TOPUP,
    request: { params: KioskTopUpRequestParamsSchema },
    responses: {
        200: jsonResponse(KioskTopUpRequestSchema, 'The request, cancelled.'),
        400: commonProblemResponses[400],
        401: commonProblemResponses[401],
        403: commonProblemResponses[403],
        404: problemResponse('No such request at the venue named in X-Clavian-Venue.', 'not-found'),
        409: problemResponse('The request is already paid and credited.', 'wallet-topup-closed'),
        429: commonProblemResponses[429],
        500: commonProblemResponses[500],
    },
});
//# sourceMappingURL=wallet-topup-requests.js.map